import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowUp, ArrowDown, Trash2, Type, Image as ImageIcon, CalendarDays,
  Quote, MousePointerClick, Minus, Save, SendHorizonal, FlaskConical, ArrowLeft, Loader2, CalendarClock, X, LibraryBig,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLOCK_DEFS = [
  { type: "text", label: "Texto", Icon: Type },
  { type: "image", label: "Imagem", Icon: ImageIcon },
  { type: "events", label: "Eventos", Icon: CalendarDays },
  { type: "library", label: "Biblioteca", Icon: LibraryBig },
  { type: "quote", label: "Citação", Icon: Quote },
  { type: "button", label: "Botão", Icon: MousePointerClick },
  { type: "divider", label: "Separador", Icon: Minus },
];

const NEW_BLOCK = {
  text: { type: "text", title: "", body: "" },
  image: { type: "image", url: "", caption: "", link_url: "" },
  events: { type: "events", event_slugs: [] },
  library: { type: "library", item_ids: [] },
  quote: { type: "quote", text: "", author: "" },
  button: { type: "button", label: "", url: "" },
  divider: { type: "divider" },
};

const inputCls = "w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm focus:outline-none focus:border-[hsl(var(--accent))]";
const lbl = "block text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1";

function BlockEditor({ block, events, libraryItems, onChange, onMove, onDelete, locked, index }) {
  const def = BLOCK_DEFS.find((d) => d.type === block.type);
  const set = (k, v) => onChange({ ...block, [k]: v });
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/newsletters/upload-image`, fd, { withCredentials: true });
      set("url", r.data.url);
      toast.success("Imagem carregada.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no upload.");
    } finally { setUploading(false); }
  };

  return (
    <div className="bg-white border border-[hsl(var(--border))] p-4" data-testid={`nl-block-${index}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--accent))]">
          <def.Icon className="h-3.5 w-3.5" /> {def.label}
        </span>
        {!locked && (
          <span className="inline-flex items-center gap-1.5">
            <button type="button" onClick={() => onMove(-1)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid={`nl-block-up-${index}`}><ArrowUp className="h-4 w-4" /></button>
            <button type="button" onClick={() => onMove(1)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid={`nl-block-down-${index}`}><ArrowDown className="h-4 w-4" /></button>
            <button type="button" onClick={onDelete} className="text-[hsl(var(--muted-foreground))] hover:text-red-700" data-testid={`nl-block-delete-${index}`}><Trash2 className="h-4 w-4" /></button>
          </span>
        )}
      </div>

      {block.type === "text" && (
        <div className="space-y-2">
          <input disabled={locked} value={block.title || ""} onChange={(e) => set("title", e.target.value)} placeholder="Título (opcional)" className={inputCls} data-testid={`nl-text-title-${index}`} />
          <textarea disabled={locked} value={block.body || ""} onChange={(e) => set("body", e.target.value)} rows={5} placeholder="Escreva o texto…" className={inputCls} data-testid={`nl-text-body-${index}`} />
        </div>
      )}

      {block.type === "image" && (
        <div className="space-y-2">
          {block.url ? (
            <img src={block.url} alt="" className="max-h-40 border border-[hsl(var(--border))]" />
          ) : (
            <p className="text-xs text-[hsl(var(--muted-foreground))]">Ainda sem imagem.</p>
          )}
          {!locked && (
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer text-[hsl(var(--primary))] underline">
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
              {block.url ? "Substituir imagem" : "Carregar imagem (JPG/PNG/WEBP, máx 5 MB)"}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                onChange={(e) => upload(e.target.files?.[0])} data-testid={`nl-image-upload-${index}`} />
            </label>
          )}
          <input disabled={locked} value={block.caption || ""} onChange={(e) => set("caption", e.target.value)} placeholder="Legenda (opcional)" className={inputCls} />
          <input disabled={locked} value={block.link_url || ""} onChange={(e) => set("link_url", e.target.value)} placeholder="Link ao clicar na imagem (opcional)" className={inputCls} />
        </div>
      )}

      {block.type === "events" && (
        <div>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2">Escolha os eventos a incluir (título, data e link automáticos):</p>
          {!events.length && <p className="text-xs text-[hsl(var(--muted-foreground))]">Sem eventos ativos no site.</p>}
          <div className="space-y-1 max-h-44 overflow-y-auto">
            {events.map((ev) => (
              <label key={ev.slug} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[hsl(var(--secondary))] px-1 py-0.5">
                <input type="checkbox" disabled={locked}
                  checked={(block.event_slugs || []).includes(ev.slug)}
                  onChange={(e) => set("event_slugs", e.target.checked
                    ? [...(block.event_slugs || []), ev.slug]
                    : (block.event_slugs || []).filter((s) => s !== ev.slug))}
                  data-testid={`nl-event-${ev.slug}`} />
                {ev.title_pt || ev.slug}
                {ev.date_str && <span className="text-xs text-[hsl(var(--muted-foreground))]">({ev.date_str})</span>}
              </label>
            ))}
          </div>
        </div>
      )}

      {block.type === "library" && (
        <div>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2">Escolha recursos da Biblioteca (título, autor, descrição e link automáticos):</p>
          {!libraryItems.length && <p className="text-xs text-[hsl(var(--muted-foreground))]">Sem recursos publicados na Biblioteca.</p>}
          <div className="space-y-1 max-h-44 overflow-y-auto">
            {libraryItems.map((it) => (
              <label key={it.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[hsl(var(--secondary))] px-1 py-0.5">
                <input type="checkbox" disabled={locked}
                  checked={(block.item_ids || []).includes(it.id)}
                  onChange={(e) => set("item_ids", e.target.checked
                    ? [...(block.item_ids || []), it.id]
                    : (block.item_ids || []).filter((s) => s !== it.id))}
                  data-testid={`nl-library-${it.id}`} />
                <span className="truncate">{it.title}</span>
                {it.author && <span className="text-xs text-[hsl(var(--muted-foreground))] truncate">— {it.author}</span>}
              </label>
            ))}
          </div>
        </div>
      )}

      {block.type === "quote" && (
        <div className="space-y-2">
          <textarea disabled={locked} value={block.text || ""} onChange={(e) => set("text", e.target.value)} rows={3} placeholder="Citação ou reflexão espiritual…" className={inputCls} data-testid={`nl-quote-text-${index}`} />
          <input disabled={locked} value={block.author || ""} onChange={(e) => set("author", e.target.value)} placeholder="Autor (ex.: Pe. José Kentenich)" className={inputCls} />
        </div>
      )}

      {block.type === "button" && (
        <div className="grid sm:grid-cols-2 gap-2">
          <input disabled={locked} value={block.label || ""} onChange={(e) => set("label", e.target.value)} placeholder="Texto do botão" className={inputCls} data-testid={`nl-button-label-${index}`} />
          <input disabled={locked} value={block.url || ""} onChange={(e) => set("url", e.target.value)} placeholder="https://…" className={inputCls} />
        </div>
      )}

      {block.type === "divider" && (
        <hr className="border-[hsl(var(--border))]" />
      )}
    </div>
  );
}

export const NewsletterEditor = ({ id, onBack }) => {
  const [nl, setNl] = useState(null);
  const [events, setEvents] = useState([]);
  const [libraryItems, setLibraryItems] = useState([]);
  const [previewHtml, setPreviewHtml] = useState("");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [scheduleAt, setScheduleAt] = useState("");
  const previewTimer = useRef(null);

  const locked = nl?.status === "sent";
  const scheduled = nl?.status === "scheduled";

  useEffect(() => {
    axios.get(`${API}/admin/newsletters/${id}`, { withCredentials: true })
      .then((r) => setNl(r.data))
      .catch(() => { toast.error("Erro a carregar."); onBack(); });
    axios.get(`${API}/events/upcoming?limit=12`).then((r) => setEvents(r.data.items || [])).catch(() => {});
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // live preview (debounced)
  useEffect(() => {
    if (!nl) return;
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      try {
        const r = await axios.post(`${API}/admin/newsletters/preview`,
          { subject: nl.subject, preheader: nl.preheader, blocks: nl.blocks }, { withCredentials: true });
        setPreviewHtml(r.data.html);
      } catch (e) { console.warn("[NewsletterEditor] preview failed:", e?.message); /* live preview: no toast while typing */ }
    }, 700);
    return () => clearTimeout(previewTimer.current);
  }, [nl?.subject, nl?.preheader, nl?.blocks]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = (upd) => { setNl((c) => ({ ...c, ...upd })); setDirty(true); };
  const setBlock = (i, b) => patch({ blocks: nl.blocks.map((x, j) => (j === i ? b : x)) });
  const moveBlock = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= nl.blocks.length) return;
    const arr = [...nl.blocks];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    patch({ blocks: arr });
  };
  const addBlock = (type) => patch({ blocks: [...nl.blocks, { ...NEW_BLOCK[type] }] });

  const save = async (silent = false) => {
    try {
      const r = await axios.patch(`${API}/admin/newsletters/${id}`,
        { subject: nl.subject, preheader: nl.preheader, blocks: nl.blocks }, { withCredentials: true });
      setNl(r.data); setDirty(false);
      if (!silent) toast.success("Rascunho guardado.");
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a guardar.");
      return false;
    }
  };

  const testSend = async () => {
    if (dirty && !(await save(true))) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/newsletters/${id}/test-send`,
        { email: testEmail || null }, { withCredentials: true });
      toast.success(`Teste enviado para ${r.data.sent_to}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no envio do teste.");
    } finally { setBusy(false); }
  };

  const sendAll = async () => {
    if (dirty && !(await save(true))) return;
    let count = "?";
    try {
      const r = await axios.get(`${API}/admin/newsletters-recipients`, { withCredentials: true });
      count = r.data.count;
    } catch { /* keep ? */ }
    if (!window.confirm(`Enviar esta newsletter a ${count} subscritor(es) agora? Esta ação não pode ser anulada.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/newsletters/${id}/send`, {}, { withCredentials: true });
      toast.success(`Newsletter enviada a ${r.data.emails_sent} subscritor(es).`);
      setNl((c) => ({ ...c, status: "sent", emails_sent: r.data.emails_sent, recipient_count: r.data.recipient_count }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar.");
    } finally { setBusy(false); }
  };

  const scheduleSend = async () => {
    if (!scheduleAt) { toast.error("Escolha a data e hora do envio."); return; }
    if (dirty && !(await save(true))) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/newsletters/${id}/schedule`,
        { scheduled_at: scheduleAt }, { withCredentials: true });
      setNl(r.data); setDirty(false);
      toast.success("Envio agendado.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao agendar.");
    } finally { setBusy(false); }
  };

  const cancelSchedule = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/newsletters/${id}/cancel-schedule`, {}, { withCredentials: true });
      setNl(r.data);
      toast.success("Agendamento cancelado.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao cancelar.");
    } finally { setBusy(false); }
  };

  const fmtLocal = (s) => (s ? s.replace("T", " às ") : "");

  if (!nl) return <p className="text-[hsl(var(--muted-foreground))] mt-8">A carregar…</p>;

  return (
    <div className="mt-6" data-testid="newsletter-editor">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid="nl-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Voltar às edições
      </button>

      {locked && (
        <div className="mt-4 bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm px-4 py-3" data-testid="nl-sent-banner">
          Enviada a {nl.emails_sent}/{nl.recipient_count} subscritores{nl.sent_at ? ` em ${new Date(nl.sent_at).toLocaleString("pt-PT")}` : ""}. Já não pode ser editada — duplique-a para criar uma nova edição.
        </div>
      )}

      {scheduled && (
        <div className="mt-4 bg-amber-50 border border-amber-200 text-amber-900 text-sm px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="nl-scheduled-banner">
          <span className="inline-flex items-center gap-2">
            <CalendarClock className="h-4 w-4" /> Envio agendado para <strong>{fmtLocal(nl.scheduled_at_local)}</strong> (hora de Lisboa). Pode continuar a editar até essa hora.
          </span>
          <button onClick={cancelSchedule} disabled={busy} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] underline hover:no-underline disabled:opacity-60" data-testid="nl-cancel-schedule">
            <X className="h-3.5 w-3.5" /> Cancelar agendamento
          </button>
        </div>
      )}

      <div className="mt-4 grid lg:grid-cols-2 gap-8">
        {/* Editor column */}
        <div className="space-y-4 min-w-0">
          <div>
            <label className={lbl}>Assunto do email *</label>
            <input disabled={locked} value={nl.subject} onChange={(e) => patch({ subject: e.target.value })}
              placeholder='Ex.: "Newsletter CIEES — março 2026"' className={inputCls} data-testid="nl-subject" />
          </div>
          <div>
            <label className={lbl}>Pré-cabeçalho (texto de pré-visualização na caixa de entrada)</label>
            <input disabled={locked} value={nl.preheader || ""} onChange={(e) => patch({ preheader: e.target.value })}
              placeholder="Uma frase curta que aparece junto ao assunto (opcional)" className={inputCls} data-testid="nl-preheader" />
          </div>

          <div className="space-y-3" data-testid="nl-blocks">
            {nl.blocks.map((b, i) => (
              <BlockEditor key={i} block={b} index={i} events={events} libraryItems={libraryItems} locked={locked}
                onChange={(nb) => setBlock(i, nb)}
                onMove={(dir) => moveBlock(i, dir)}
                onDelete={() => patch({ blocks: nl.blocks.filter((_, j) => j !== i) })} />
            ))}
            {!nl.blocks.length && (
              <p className="text-sm text-[hsl(var(--muted-foreground))] bg-white border border-dashed border-[hsl(var(--border))] p-6 text-center">
                Comece por adicionar um bloco de conteúdo abaixo.
              </p>
            )}
          </div>

          {!locked && (
            <div className="flex flex-wrap gap-2 pt-1">
              {BLOCK_DEFS.map((d) => (
                <button key={d.type} type="button" onClick={() => addBlock(d.type)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-[hsl(var(--border))] text-xs hover:border-[hsl(var(--accent))] transition-colors"
                  data-testid={`nl-add-${d.type}`}>
                  <d.Icon className="h-3.5 w-3.5 text-[hsl(var(--accent))]" /> {d.label}
                </button>
              ))}
            </div>
          )}

          {!locked && (
            <div className="border-t border-[hsl(var(--border))] pt-4 space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <button onClick={() => save()} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-[hsl(var(--primary))] text-[hsl(var(--primary))] text-xs uppercase tracking-[0.18em] hover:bg-[hsl(var(--secondary))]" data-testid="nl-save">
                  <Save className="h-3.5 w-3.5" /> Guardar rascunho{dirty ? " *" : ""}
                </button>
                <div className="inline-flex items-center gap-2">
                  <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="Email de teste (por omissão: o seu)" className="bg-white border border-[hsl(var(--border))] px-3 py-2 text-xs w-56" data-testid="nl-test-email" />
                  <button onClick={testSend} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-[hsl(var(--border))] text-xs uppercase tracking-[0.18em] hover:border-[hsl(var(--accent))] disabled:opacity-60" data-testid="nl-test-send">
                    <FlaskConical className="h-3.5 w-3.5" /> Enviar teste
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button onClick={sendAll} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.18em] hover:bg-[#093630] disabled:opacity-60" data-testid="nl-send-all">
                  <SendHorizonal className="h-3.5 w-3.5" /> {busy ? "A enviar…" : "Enviar a todos os subscritores"}
                </button>
                {!scheduled && (
                  <div className="inline-flex items-center gap-2">
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">ou</span>
                    <input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)}
                      className="bg-white border border-[hsl(var(--border))] px-3 py-2 text-xs" data-testid="nl-schedule-at" />
                    <button onClick={scheduleSend} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-[hsl(var(--accent))] text-[hsl(var(--primary))] text-xs uppercase tracking-[0.18em] hover:bg-[hsl(var(--secondary))] disabled:opacity-60" data-testid="nl-schedule">
                      <CalendarClock className="h-3.5 w-3.5" /> Agendar envio
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Preview column */}
        <div className="min-w-0">
          <div className={lbl}>Pré-visualização do email</div>
          <div className="border border-[hsl(var(--border))] bg-[hsl(var(--secondary))]" style={{ height: "75vh" }}>
            <iframe title="Pré-visualização" srcDoc={previewHtml} className="w-full h-full" data-testid="nl-preview-frame" sandbox="" />
          </div>
        </div>
      </div>
    </div>
  );
};
